import { extractInternalParkingSnapshot } from "./internalParkingData";

export type ParkingListItem = {
  id: string | number | null;
  name: string | null;
  address: string | null;
  city: string | null;
  stats?: ParkingListStats | null;
  spotOccupancy?: ParkingSpotOccupancyMap | null;
  priceTemplateId?: string | null;
  coords?: [number, number] | null;
  space_regular: number | null;
  space_handi: number | null;
  space_family: number | null;
  space_disabled: number | null;
  space_ev: number | null;
};

export type ParkingListStats = {
  total: number | null;
  occupied: number | null;
  free: number | null;
};

export type ParkingSpotOccupancyMap = Record<string, 0 | 1>;

export function normalizeParkingListResponse(payload: unknown): ParkingListItem[] {
  const source = extractParkingListArray(payload);
  if (!source) {
    return [];
  }
  return source
    .map((entry) => normalizeParkingListEntry(entry))
    .filter((entry): entry is ParkingListItem => entry !== null);
}

function extractParkingListArray(payload: unknown): unknown[] | null {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const candidates = [record["_data_"], record._data_, record.data];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return null;
}

function normalizeParkingListEntry(entry: unknown): ParkingListItem | null {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const rawId = record.id;
  const normalizedId =
    typeof rawId === "string" && rawId.trim().length > 0
      ? rawId.trim()
      : typeof rawId === "number" && Number.isFinite(rawId)
        ? rawId
        : null;

  const name = typeof record.name === "string" && record.name.trim().length > 0 ? record.name.trim() : null;
  const address = typeof record.address === "string" && record.address.trim().length > 0 ? record.address.trim() : null;
  const city =
    typeof record.city === "string" && record.city.trim().length > 0
      ? record.city.trim()
      : typeof record.city === "number" && Number.isFinite(record.city)
        ? String(record.city)
        : null;
  const priceTemplateId = normalizeIdValue(
    record.price_template_id ?? record.price_id ?? record.type_id ?? record.parking_type_id ?? record.parking_price_id,
  );
  const coords = normalizeCoords(record);

  const stats = normalizeParkingResultsStats(record.results ?? record.result);
  const spotOccupancy = normalizeParkingSpotOccupancy(record.results ?? record.result);

  return {
    id: normalizedId,
    name,
    address,
    city,
    stats,
    spotOccupancy,
    priceTemplateId,
    coords,
    space_regular: normalizeNumericValue(record.space_regular),
    space_handi: normalizeNumericValue(record.space_handi),
    space_family: normalizeNumericValue(record.space_family),
    space_disabled: normalizeNumericValue(record.space_disabled),
    space_ev: normalizeNumericValue(record.space_ev),
  };
}

function normalizeNumericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().replace(",", ".");
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const normalizeIdValue = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const parseCoordinate = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().replace(",", ".");
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeCoords = (record: Record<string, unknown>): [number, number] | null => {
  const latCandidate =
    record.lat ?? record.latitude ?? record.Latitude ?? record.latitide ?? record.latitute ?? record.y ?? record.Y;
  const lngCandidate = record.lng ?? record.lon ?? record.longitude ?? record.Lon ?? record.long ?? record.x ?? record.X;
  const lat = parseCoordinate(latCandidate);
  const lng = parseCoordinate(lngCandidate);
  if (lat === null || lng === null) {
    return null;
  }
  return [Number(lng.toFixed(6)), Number(lat.toFixed(6))];
};

const tryParseJson = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const parseStatNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().replace(",", ".");
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseOccupancyArray = (value: unknown): { total: number; occupied: number } | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  let total = 0;
  let occupied = 0;
  for (const entry of value) {
    const parsed = parseStatNumber(entry);
    if (parsed === null) {
      continue;
    }
    total += 1;
    if (parsed > 0) {
      occupied += 1;
    }
  }
  return total > 0 ? { total, occupied } : null;
};

const parseNumberingScheme = (value: unknown): number | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  let total = 0;
  for (const entry of value) {
    const parsed = parseStatNumber(entry);
    if (parsed === null) {
      continue;
    }
    total += 1;
  }
  return total > 0 ? total : null;
};

const extractParkingDetailArray = (record: Record<string, unknown>): Array<Record<string, unknown>> | null => {
  const detail = record["parking_detail"] ?? record.parking_detail ?? record.parkingDetail ?? record.detail ?? null;
  if (Array.isArray(detail)) {
    return detail.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object");
  }
  return null;
};

const buildTotalsFromDetail = (record: Record<string, unknown>): { occupied: number; total: number } | null => {
  const detailArray = extractParkingDetailArray(record);
  if (!detailArray) {
    return null;
  }
  let occupied = 0;
  let total = 0;
  for (const detail of detailArray) {
    const occupancy = parseOccupancyArray((detail as Record<string, unknown>).occupancy);
    const numberingTotal = parseNumberingScheme((detail as Record<string, unknown>).numbering_scheme);
    const resolvedTotal = occupancy?.total ?? numberingTotal ?? 0;
    const resolvedOccupied = occupancy?.occupied ?? 0;
    occupied += resolvedOccupied;
    total += resolvedTotal;
  }
  if (total === 0 && occupied === 0) {
    return null;
  }
  return { occupied, total };
};

const shouldSkipSpotKey = (key: string): boolean => {
  const normalized = key.toLowerCase();
  return (
    normalized.startsWith("total_") ||
    normalized === "total" ||
    normalized === "free" ||
    normalized === "report_type" ||
    normalized === "reporttype" ||
    normalized === "timestamp" ||
    normalized === "time" ||
    normalized === "ctime" ||
    normalized === "event" ||
    normalized === "device" ||
    normalized === "parking_detail" ||
    normalized === "parkingdetail" ||
    normalized === "detail"
  );
};

const normalizeSpotOccupancyValue = (value: unknown): 0 | 1 | null => {
  const direct = parseStatNumber(value);
  if (direct !== null) {
    return direct > 0 ? 1 : 0;
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const nested = parseStatNumber(record.occupancy ?? record.occupied ?? record.value ?? record.status);
  if (nested === null) {
    return null;
  }

  return nested > 0 ? 1 : 0;
};

const buildSpotOccupancyMap = (
  record: Record<string, unknown>,
): { occupancyMap: ParkingSpotOccupancyMap; occupied: number; total: number } => {
  const occupancyMap: ParkingSpotOccupancyMap = {};
  let occupiedCount = 0;
  let totalCount = 0;

  Object.entries(record).forEach(([key, value]) => {
    if (!key || typeof key !== "string" || shouldSkipSpotKey(key)) {
      return;
    }
    const occupancy = normalizeSpotOccupancyValue(value);
    if (occupancy === null) {
      return;
    }
    occupancyMap[key] = occupancy;
    totalCount += 1;
    if (occupancy > 0) {
      occupiedCount += 1;
    }
  });

  return {
    occupancyMap,
    occupied: occupiedCount,
    total: totalCount,
  };
};

const buildTotalsFromSpotMap = (record: Record<string, unknown>): { occupied: number; total: number } | null => {
  const spotMap = buildSpotOccupancyMap(record);
  return spotMap.total > 0 ? { occupied: spotMap.occupied, total: spotMap.total } : null;
};

const buildStatsFromRecord = (record: Record<string, unknown>): ParkingListStats | null => {
  const totalOccupied = parseStatNumber(record.total_occupied ?? record.totalOccupied ?? record.occupied);
  const totalFree = parseStatNumber(record.total_available ?? record.totalAvailable ?? record.free);
  const totalValue = parseStatNumber(record.total ?? record.total_spaces ?? record.totalSpaces);
  const detailTotals = buildTotalsFromDetail(record);
  const spotMapTotals = buildTotalsFromSpotMap(record);

  const occupiedCandidate = totalOccupied ?? detailTotals?.occupied ?? spotMapTotals?.occupied ?? null;
  const totalCandidate = totalValue ?? detailTotals?.total ?? spotMapTotals?.total ?? null;
  const freeCandidate = totalFree ?? null;

  let occupied = occupiedCandidate;
  let total = totalCandidate;
  let free = freeCandidate;

  if (total === null && occupied !== null && free !== null) {
    total = occupied + free;
  }
  if (free === null && total !== null && occupied !== null) {
    free = Math.max(total - occupied, 0);
  }
  if (occupied === null && total !== null && free !== null) {
    occupied = Math.max(total - free, 0);
  }

  const hasAnyValue = occupied !== null || total !== null || free !== null;
  if (!hasAnyValue) {
    return null;
  }

  const normalize = (value: number | null) => {
    if (value === null) {
      return null;
    }
    const clamped = Math.max(0, Math.round(value));
    return Number.isFinite(clamped) ? clamped : null;
  };

  return {
    occupied: normalize(occupied),
    total: normalize(total),
    free: normalize(free),
  };
};

const mergeStats = (stats: ParkingListStats): ParkingListStats => {
  let { occupied, total, free } = stats;
  if (total !== null && occupied !== null && free === null) {
    free = Math.max(total - occupied, 0);
  } else if (occupied !== null && free !== null && total === null) {
    total = Math.max(occupied + free, 0);
  } else if (total !== null && free !== null && occupied === null) {
    occupied = Math.max(total - free, 0);
  }
  return {
    occupied,
    total,
    free,
  };
};

const pickBestStats = (candidates: ParkingListStats[]): ParkingListStats | null => {
  if (!candidates.length) {
    return null;
  }
  const scoreStats = (stats: ParkingListStats) => {
    const filled =
      (stats.total === null ? 0 : 1) + (stats.occupied === null ? 0 : 1) + (stats.free === null ? 0 : 1);
    return { filled, total: stats.total ?? 0 };
  };

  return candidates.reduce<ParkingListStats | null>((best, current) => {
    if (!best) {
      return current;
    }
    const bestScore = scoreStats(best);
    const currentScore = scoreStats(current);
    if (currentScore.filled > bestScore.filled) {
      return current;
    }
    if (currentScore.filled === bestScore.filled && currentScore.total > bestScore.total) {
      return current;
    }
    return best;
  }, null);
};

const collectStatsCandidates = (payload: unknown, depth = 0): ParkingListStats[] => {
  if (depth > 4 || payload === null || typeof payload === "undefined") {
    return [];
  }
  if (typeof payload === "string") {
    const parsed = tryParseJson(payload.trim());
    return parsed ? collectStatsCandidates(parsed, depth + 1) : [];
  }
  if (Array.isArray(payload)) {
    return payload.flatMap((entry) => collectStatsCandidates(entry, depth + 1));
  }
  if (typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const candidates: ParkingListStats[] = [];
  const directStats = buildStatsFromRecord(record);
  if (directStats) {
    candidates.push(directStats);
  }

  const nestedCandidates = [
    record.results,
    record.result,
    record._data_,
    record.data,
    record.payload,
    record.body,
    record.response,
  ];
  nestedCandidates.forEach((nested) => {
    candidates.push(...collectStatsCandidates(nested, depth + 1));
  });

  return candidates;
};

const normalizeParkingResultsStats = (payload: unknown): ParkingListStats | null => {
  const candidates = collectStatsCandidates(payload);
  const best = pickBestStats(candidates);
  if (!best) {
    return null;
  }
  return mergeStats(best);
};

const collectSpotOccupancyCandidates = (payload: unknown, depth = 0): ParkingSpotOccupancyMap[] => {
  if (depth > 4 || payload === null || typeof payload === "undefined") {
    return [];
  }
  if (typeof payload === "string") {
    const parsed = tryParseJson(payload.trim());
    return parsed ? collectSpotOccupancyCandidates(parsed, depth + 1) : [];
  }
  if (Array.isArray(payload)) {
    return payload.flatMap((entry) => collectSpotOccupancyCandidates(entry, depth + 1));
  }
  if (typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const directSpotMap = buildSpotOccupancyMap(record);
  const candidates: ParkingSpotOccupancyMap[] = directSpotMap.total > 0 ? [directSpotMap.occupancyMap] : [];
  const nestedCandidates = [
    record.results,
    record.result,
    record._data_,
    record.data,
    record.payload,
    record.body,
    record.response,
  ];

  nestedCandidates.forEach((nested) => {
    candidates.push(...collectSpotOccupancyCandidates(nested, depth + 1));
  });

  return candidates;
};

const pickBestSpotOccupancy = (candidates: ParkingSpotOccupancyMap[]): ParkingSpotOccupancyMap | null => {
  if (!candidates.length) {
    return null;
  }

  return candidates.reduce<ParkingSpotOccupancyMap | null>((best, current) => {
    if (!best) {
      return current;
    }
    return Object.keys(current).length > Object.keys(best).length ? current : best;
  }, null);
};

const normalizeParkingSpotOccupancy = (payload: unknown): ParkingSpotOccupancyMap | null => {
  const candidates = collectSpotOccupancyCandidates(payload);
  const internalSnapshot = extractInternalParkingSnapshot(payload);
  if (internalSnapshot && Object.keys(internalSnapshot.spotOccupancy).length) {
    candidates.push(internalSnapshot.spotOccupancy);
  }
  return pickBestSpotOccupancy(candidates);
};
